/**
 * JSON messages sent from the API / Bridge DO to the CLI over the bridge WebSocket.
 * (Inbound on the CLI bridge connection.)
 */

/** `preview_environment_control` payloads may be E2EE-wrapped; fields appear after decrypt. */
export interface ApiToBridgePreviewEnvironmentControlMessage {
  type: 'preview_environment_control';
  environmentId?: string;
  action?: 'start' | 'stop';
  [key: string]: unknown;
}

export interface ApiToBridgeDeploySessionToPreviewMessage {
  type: 'deploy_session_to_preview';
  id?: string;
  sessionId?: string;
  environmentId?: string;
}

export interface ApiToBridgeAuthTokenMessage {
  type: 'auth_token';
  token: string;
}

export interface ApiToBridgeBridgeIdentifiedMessage {
  type: 'bridge_identified';
  bridgeName: string;
  proxyPorts?: number[];
  previewEnvironments?: unknown;
}

export interface ApiToBridgeInstalledAgentAuthSyncMessage {
  type: 'installed_agent_auth_sync';
  entries?: Array<{ envVar: string; token: string }>;
}

/** Compact server→CLI heartbeat ack (`t:'ha', s` on the wire; normalized before parse). */
export interface ApiToBridgeHeartbeatAckMessage {
  type: 'ha';
  s: number;
}

export interface ApiToBridgePreviewEnvironmentsConfigMessage {
  type: 'preview_environments_config';
  previewEnvironments?: unknown;
  proxyPorts?: number[];
}

export interface ApiToBridgeAgentConfigMessage {
  type: 'agent_config';
  agents: Array<{ type: string }>;
}

export interface ApiToBridgePromptQueueStateMessage {
  type: 'prompt_queue_state';
  queues?: Record<string, unknown[]>;
}

export interface ApiToBridgePromptMessage {
  type: 'prompt';
  id?: string;
  sessionId?: string;
  runId?: string;
  prompt?: unknown;
  /** R2-backed images; CLI downloads and sends ACP image blocks when the agent supports them. */
  attachments?: Array<{ attachmentId: string; mimeType: string }>;
  isNewSession?: boolean;
  /** From Session DO when set; otherwise omitted. */
  sessionParent?: 'bridge_root' | 'worktrees_root';
  sessionParentPath?: string;
  /** Base branch per repo rel path when creating session worktrees. */
  worktreeBaseBranches?: Record<string, string>;
  agentType?: string;
  agentId?: string | null;
  mode?: string;
  /** Agent-specific options for this prompt turn (e.g. Claude reads `claude_permission_mode`). */
  agentConfig?: Record<string, unknown>;
  followUpCatalogPromptId?: string;
  [key: string]: unknown;
}

export type ApiToBridgeSessionGitRequestAction =
  | 'status'
  | 'push'
  | 'commit'
  | 'list_changes'
  | 'file_diff';

/** Nested working-tree / commit diff params for `list_changes` and `file_diff`. */
export interface ApiToBridgeSessionGitChangesPayload {
  repoRelPath?: string;
  view?: 'working' | 'commit';
  commitSha?: string;
  recentCommitsLimit?: number | string;
  file?: {
    workspaceRelPath?: string;
    change?: string;
    movedFromWorkspaceRelPath?: string;
  };
}

export interface ApiToBridgeSessionGitRequestMessage {
  type: 'session_git_request';
  id?: string;
  sessionId?: string;
  action?: ApiToBridgeSessionGitRequestAction;
  branch?: string;
  message?: string;
  pushAfterCommit?: boolean;
  changes?: ApiToBridgeSessionGitChangesPayload;
  /** @deprecated Use `changes.repoRelPath` */
  changesRepoRelPath?: string;
  /** @deprecated Use `changes.view` */
  changesView?: string;
  /** @deprecated Use `changes.commitSha` */
  changesCommitSha?: string;
  /** @deprecated Use `changes.recentCommitsLimit` */
  changesRecentCommitsLimit?: number | string;
}

export interface ApiToBridgeRenameSessionBranchMessage {
  type: 'rename_session_branch';
  sessionId?: string;
  newBranch?: string;
}

export interface ApiToBridgeSessionArchivedMessage {
  type: 'session_archived';
  sessionId?: string;
}

export interface ApiToBridgeSessionDiscardedMessage {
  type: 'session_discarded';
  sessionId?: string;
}

export interface ApiToBridgeRevertTurnSnapshotMessage {
  type: 'revert_turn_snapshot';
  id?: string;
  sessionId?: string;
  turnId?: string;
}

/**
 * Resolves a pending session/agent request (permission, plan, question, etc.) on the CLI.
 * Wire `type` remains `cursor_request_response` for compatibility with deployed bridges/agents.
 */
export interface ApiToBridgeSessionRequestResponseMessage {
  type: 'cursor_request_response';
  requestId?: string;
  result?: unknown;
}

export interface ApiToBridgeSkillCallMessage {
  type: 'skill_call';
  id?: string;
  skillId?: string;
  operationId?: string;
  params?: Record<string, unknown>;
}

export interface ApiToBridgeFileBrowserRequestMessage {
  type: 'file_browser_request';
  id?: string;
  path?: string;
  op?: string;
  sessionId?: string;
  source?: 'filesystem' | 'git_branch';
  repoRelPath?: string;
  branch?: string;
  startLine?: number;
  endLine?: number;
  lineOffset?: number;
  lineChunkSize?: number;
  encoding?: 'utf8' | 'base64';
}

export interface ApiToBridgeFileBrowserSearchMessage {
  type: 'file_browser_search';
  id?: string;
  q?: string;
  sessionId?: string;
}

export interface ApiToBridgeFileBrowserCodeNavMessage {
  type: 'code_nav';
  id?: string;
  op?: 'definition' | 'definitions' | 'references' | 'symbols';
  path?: string;
  /** 1-based line number. */
  line?: number;
  /** 0-based column. */
  column?: number;
  sessionId?: string;
}

export interface ApiToBridgeSkillLayoutRequestMessage {
  type: 'skill_layout_request';
  id?: string;
}

export interface ApiToBridgeInstallSkillsMessage {
  type: 'install_skills';
  id?: string;
  targetDir?: string;
  items?: unknown;
}

export interface ApiToBridgeInstallAgentMessage {
  type: 'install_agent';
  id?: string;
  processId?: string;
  agentId?: string;
  tokenId?: string;
  agentType?: string;
  authToken?: string;
}

export interface ApiToBridgeRefreshLocalSkillsMessage {
  type: 'refresh_local_skills';
}

export interface ApiToBridgeBridgeGitContextRequestMessage {
  type: 'bridge_git_context_request';
  id?: string;
}

export interface ApiToBridgeListRepoBranchesRequestMessage {
  type: 'list_repo_branches_request';
  id?: string;
  repoRelPath?: string;
}

export type ApiToBridgeMessage =
  | ApiToBridgeAuthTokenMessage
  | ApiToBridgeBridgeIdentifiedMessage
  | ApiToBridgeInstalledAgentAuthSyncMessage
  | ApiToBridgeHeartbeatAckMessage
  | ApiToBridgePreviewEnvironmentsConfigMessage
  | ApiToBridgePreviewEnvironmentControlMessage
  | ApiToBridgeDeploySessionToPreviewMessage
  | ApiToBridgeAgentConfigMessage
  | ApiToBridgePromptQueueStateMessage
  | ApiToBridgePromptMessage
  | ApiToBridgeSessionGitRequestMessage
  | ApiToBridgeRenameSessionBranchMessage
  | ApiToBridgeSessionArchivedMessage
  | ApiToBridgeSessionDiscardedMessage
  | ApiToBridgeRevertTurnSnapshotMessage
  | ApiToBridgeSessionRequestResponseMessage
  | ApiToBridgeSkillCallMessage
  | ApiToBridgeFileBrowserRequestMessage
  | ApiToBridgeFileBrowserSearchMessage
  | ApiToBridgeFileBrowserCodeNavMessage
  | ApiToBridgeSkillLayoutRequestMessage
  | ApiToBridgeInstallSkillsMessage
  | ApiToBridgeInstallAgentMessage
  | ApiToBridgeRefreshLocalSkillsMessage
  | ApiToBridgeBridgeGitContextRequestMessage
  | ApiToBridgeListRepoBranchesRequestMessage;

export const API_TO_BRIDGE_MESSAGE_TYPES = [
  'auth_token',
  'bridge_identified',
  'installed_agent_auth_sync',
  'ha',
  'preview_environments_config',
  'preview_environment_control',
  'deploy_session_to_preview',
  'agent_config',
  'prompt_queue_state',
  'prompt',
  'session_git_request',
  'rename_session_branch',
  'session_archived',
  'session_discarded',
  'revert_turn_snapshot',
  'cursor_request_response',
  'skill_call',
  'file_browser_request',
  'file_browser_search',
  'code_nav',
  'skill_layout_request',
  'install_skills',
  'install_agent',
  'refresh_local_skills',
  'bridge_git_context_request',
  'list_repo_branches_request',
] as const satisfies readonly ApiToBridgeMessage['type'][];

export type ApiToBridgeMessageType = (typeof API_TO_BRIDGE_MESSAGE_TYPES)[number];

const API_TO_BRIDGE_TYPE_SET = new Set<string>(API_TO_BRIDGE_MESSAGE_TYPES);

/**
 * Narrow unknown WebSocket JSON to a known API→bridge message, or null if malformed / unknown `type`.
 */
export function parseApiToBridgeMessage(
  data: unknown,
  log?: (message: string) => void,
): ApiToBridgeMessage | null {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) return null;
  const t = (data as { type?: unknown }).type;
  if (typeof t !== 'string' || !API_TO_BRIDGE_TYPE_SET.has(t)) {
    if (typeof t === 'string') {
      log?.(`[Bridge service] unhandled message type: ${t}`);
    }
    return null;
  }
  if (t === 'ha') {
    const s = (data as { s?: unknown }).s;
    if (typeof s !== 'number' || !Number.isFinite(s)) return null;
  }
  return data as ApiToBridgeMessage;
}
